import { isUuid } from "../MiscUtils";
import { sanitizeNamespace } from "./GraphMisc";
import type { GraphNode, ParsedRuleGraph } from "./GraphTypes";

export const GRAPH_LOOKUP_CACHE_KEY = Symbol("GraphLookupCache");

type CacheableGraph = {
  [GRAPH_LOOKUP_CACHE_KEY]?: Record<string, string>;
};

export const cleanGraphDuplicates = (
  graph: ParsedRuleGraph,
  documents: any,
  enableNamespaces?: boolean,
): ParsedRuleGraph => {
  const seen: Record<string, string> = {};
  for (const n of graph.nodes()) {
    const node = graph.node(n);
    if (!node) {
      continue;
    }
    const key = enableNamespaces ? `${sanitizeNamespace(node.definedIn as any)}/${node.description}` : node.description;
    if (!key) {
      continue;
    }

    const existing = seen[key];
    if (existing) {
      const duplicateNodeRegex = new RegExp(n, "g");
      const replaceAttr = (id: string, to: string) => {
        const node = graph.node(id);
        Object.assign(node, JSON.parse(JSON.stringify(node).replace(duplicateNodeRegex, to)));
      };

      const predecessors = graph.predecessors(n) as any;
      if (predecessors) {
        for (const p of predecessors) {
          graph.setEdge({ v: p, w: existing });
          replaceAttr(p, existing);
        }
      }
      const successors = graph.successors(n) as any;
      if (successors) {
        for (const s of successors) {
          graph.setEdge({ v: existing, w: s });
          replaceAttr(s, existing);
        }
      }

      graph.removeNode(n);
    } else {
      seen[key] = n;
    }
  }
  return graph;
};

export const iterateGraphNodes = (graph: any, func: (node: GraphNode) => void) => {
  for (const node of iterateGraphNodeValues(graph)) {
    func(node);
  }
};

export const getGraphValue = (graph: any): any => {
  if (!graph) {
    return undefined;
  }
  if (typeof graph.graph === "function") {
    return graph.graph();
  }
  return graph.value;
};

export const setGraphValue = (graph: any, value: any): void => {
  if (!graph) {
    return;
  }
  if (typeof graph.setGraph === "function") {
    graph.setGraph(value);
    return;
  }
  graph.value = value;
};

export function* iterateGraphNodeValues(graph: any): IterableIterator<GraphNode> {
  if (!graph?.nodes) {
    return;
  }

  if (typeof graph.node === "function") {
    for (const nodeId of graph.nodes()) {
      const node = graph.node(nodeId);
      if (node) {
        yield node;
      }
    }
    return;
  }

  if (Array.isArray(graph.nodes)) {
    for (const node of graph.nodes) {
      if (node?.value) {
        yield node.value;
      }
    }
  }
}

export const getGraphNodeByNodeKey = (graph: any, key: string): GraphNode | undefined => {
  if (!graph?.nodes) {
    return undefined;
  }

  if (typeof graph.node === "function") {
    const node = graph.node(key) as GraphNode | undefined;
    if (node) {
      return node;
    }
    for (const value of iterateGraphNodeValues(graph)) {
      if (value.path === key || value.id === key) {
        return value;
      }
    }
    return undefined;
  }

  if (Array.isArray(graph.nodes)) {
    for (const node of graph.nodes) {
      const value = node?.value;
      if (value && (node.v === key || value.path === key || value.id === key)) {
        return value;
      }
    }
  }

  return undefined;
};

export const getGraphLookupCache = (graph: any): Record<string, string> | undefined => {
  const cacheableGraph = graph as CacheableGraph;
  if (cacheableGraph[GRAPH_LOOKUP_CACHE_KEY]) {
    return cacheableGraph[GRAPH_LOOKUP_CACHE_KEY];
  }
  if (!Object.isExtensible(graph)) {
    return undefined;
  }
  const cache = {};
  cacheableGraph[GRAPH_LOOKUP_CACHE_KEY] = cache;
  return cache;
};

export const cacheGraphNodeLookup = (graph: any, key: string, node: GraphNode | undefined): void => {
  const cache = getGraphLookupCache(graph);
  if (cache && node) {
    cache[key] = node.path ?? node.id;
  }
};

export const getCachedGraphNode = (graph: any, key: string): GraphNode | undefined => {
  const cachedNodeKey = getGraphLookupCache(graph)?.[key];
  return cachedNodeKey ? getGraphNodeByNodeKey(graph, cachedNodeKey) : undefined;
};

const cacheNodeAliases = (graph: any, node: GraphNode) => {
  const entityPath = node.path?.split("/") || [];
  entityPath.pop();
  if (node.description) {
    cacheGraphNodeLookup(graph, [...entityPath, node.description].join("/"), node);
  }
  if (node.publicId) {
    cacheGraphNodeLookup(graph, [...entityPath, node.publicId].join("/"), node);
  }
};

const lookupOne = (graph: any, key: string): GraphNode | undefined => {
  let node = getGraphNodeByNodeKey(graph, key) as any;
  if (node) {
    return node;
  }

  if (isUuid(key)) {
    return undefined;
  }

  if (typeof graph.setNode === "function" && !graph.__lookupCacheSetNodePatched) {
    const originalSetNode = graph.setNode;
    graph.setNode = (id: string, value: any) => {
      cacheNodeAliases(graph, value);
      return originalSetNode.call(graph, id, value);
    };
    graph.__lookupCacheSetNodePatched = true;
  }

  node = getCachedGraphNode(graph, key);
  if (node) {
    return node;
  }

  let result: any = undefined;
  let cmp = key;
  let path: string | undefined;
  if (key.includes("/")) {
    const info = key.split("/");
    //@ts-ignore
    cmp = info.pop(); // compare against the last element (most likely the attribute)
    path = info.join("/");
  }

  for (const n of iterateGraphNodeValues(graph)) {
    if (!n) {
      continue;
    }
    cacheNodeAliases(graph, n);
    if (n.publicId === key || n.description === key || n.path === key) {
      result = n;
    } else if (path && (n.id === cmp || n.publicId === cmp || n.description === cmp)) {
      if (n.path === `${path}/${n.id}`) {
        result = n;
      }
    }
    if (result) {
      break;
    }
  }

  return result;
};

/**
 * Get a node from the graph by key. When traverse is true and the exact key is not found,
 * walks up the path by removing one entity/instance pair at a time and tries parentPath/attr
 * (e.g. "employees/1/roles/2/attr" -> "employees/1/attr" -> "attr"). Prefers the closest match.
 */
export const getNode = (key: string, graph: any, traverse?: boolean): GraphNode | undefined => {
  let node = lookupOne(graph, key);
  if (node) {
    return node;
  }

  // Paths are entity/instance/attribute with entity/instance in pairs; traverse walks up by removing one pair at a time
  if (traverse && key.includes("/")) {
    const parts = key.split("/");
    const attr = parts.pop();
    if (!attr) {
      return undefined;
    }
    // Already tried full key; pop entity/instance pairs and try each parent path (closer match first)
    while (parts.length >= 2) {
      parts.pop();
      parts.pop();
      const path = parts.join("/");
      node = lookupOne(graph, path ? `${path}/${attr}` : attr);
      if (node) {
        return node;
      }
    }
    if (!node) {
      node = lookupOne(graph, attr);
    }
  }

  return node;
};
